const API_BASE = import.meta.env.VITE_API_BASE ?? '';

function getToken(): string | null {
  return localStorage.getItem('erixone_token') ?? localStorage.getItem('ericone_token');
}

export function setToken(token: string): void {
  localStorage.setItem('erixone_token', token);
  localStorage.setItem('ericone_token', token);
}

export function clearToken(): void {
  localStorage.removeItem('erixone_token');
  localStorage.removeItem('ericone_token');
}

type RequestOptions = Omit<RequestInit, 'body'> & { body?: unknown };

async function request<T = any>(path: string, options: RequestOptions = {}): Promise<T> {
  const token = getToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string> ?? {}),
  };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers,
    body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText })) as { error?: string };
    throw new Error(err.error ?? `HTTP ${res.status}`);
  }

  return res.json() as Promise<T>;
}

export const api = {
  // Auth
  login: (credentials: string | { email?: string; password?: string; token?: string }) => {
    const body = typeof credentials === 'string' ? { token: credentials } : credentials;
    return request<{ token: string; tenant: any }>('/api/auth/login', { method: 'POST', body });
  },

  register: (payload: { name: string; email?: string; password?: string }) =>
    request<{ token: string; tenant: any }>('/api/auth/register', { method: 'POST', body: payload }),
  
  me: () => request<any>('/api/auth/me'),
  logout: () => {
    clearToken();
    return Promise.resolve();
  },

  get: (path: string) => request<any>(path),
  post: (path: string, body: any) => request<any>(path, { method: 'POST', body }),
  put: (path: string, body: any) => request<any>(path, { method: 'PUT', body }),
  delete: (path: string) => request<any>(path, { method: 'DELETE' }),

  // Documents
  createFolder: (name: string, parentId?: string, vaultId?: string | null, diskType?: 'cloud' | 'digitization') => 
    request<{ id: string, status: string, disk_type?: string }>('/api/documents/folders', { method: 'POST', body: { name, parent_id: parentId, vault_id: vaultId, disk_type: diskType || 'cloud' } }),
    
  listDocuments: (vaultId?: string | null | 'shared', noCache?: boolean, parentId?: string, diskType?: 'cloud' | 'digitization' | 'hybrid' | 'all') => {
    const params = new URLSearchParams();
    if (vaultId) params.append('vault_id', vaultId);
    if (noCache) params.append('nocache', '1');
    if (parentId) params.append('parent_id', parentId);
    if (diskType && diskType !== 'all') params.append('disk_type', diskType);
    const qs = params.toString();
    return request<{ documents: any[] }>(`/api/documents${qs ? '?' + qs : ''}`);
  },
  
  uploadDocument: async (file: File, parentId?: string, extractorId?: string, vaultId?: string | null, ocrMode?: string, diskType: 'cloud' | 'digitization' = 'cloud') => {
    const token = getToken();
    const headers: Record<string, string> = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;

    // Fast Path: Direct Stream Upload qua Presigned URL (SPEC-CF-QLW-ENTERPRISE-V1 §3.9 & §7)
    try {
      const ticketRes = await fetch(`${API_BASE}/api/documents/presigned-url`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...headers },
        body: JSON.stringify({
          filename: file.name,
          file_size: file.size,
          parent_id: parentId,
          extractor_id: extractorId,
          vault_id: vaultId !== 'shared' ? vaultId : null,
          ocr_mode: ocrMode,
          disk_type: diskType
        })
      });

      if (ticketRes.ok) {
        const ticket = await ticketRes.json() as { document_id: string; upload_url: string };
        const uploadRes = await fetch(`${API_BASE}${ticket.upload_url}`, {
          method: 'PUT',
          headers: {
            'Content-Type': file.type || 'application/octet-stream',
            ...headers
          },
          body: file
        });
        if (uploadRes.ok) {
          return await uploadRes.json() as { document_id: string; status: string; status_url?: string; stream_url?: string; disk_type?: string };
        }
      }
    } catch {
      // Fallback sang multipart form upload chuẩn nếu direct upload gặp sự cố
    }

    const formData = new FormData();
    formData.append('file', file);
    if (parentId) formData.append('parent_id', parentId);
    if (extractorId) formData.append('extractor_id', extractorId);
    if (vaultId && vaultId !== 'shared') formData.append('vault_id', vaultId);
    if (ocrMode) formData.append('ocr_mode', ocrMode);
    formData.append('disk_type', diskType);

    const res = await fetch(`${API_BASE}/api/documents/upload`, {
      method: 'POST',
      headers,
      body: formData,
    });
    
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: res.statusText })) as { error?: string };
      throw new Error(err.error ?? `HTTP ${res.status}`);
    }
    
    return res.json() as Promise<{ document_id: string; status: string; status_url?: string; stream_url?: string; disk_type?: string }>;
  },

  digitizeDocument: (id: string, ocrMode?: string) =>
    request<{ document_id: string; status: string; disk_type: string }>(`/api/documents/${id}/digitize`, {
      method: 'POST',
      body: { ocr_mode: ocrMode }
    }),

  undigitizeDocument: (id: string) =>
    request<{ success: boolean; document_id: string; disk_type: string }>(`/api/documents/${id}/digitize`, {
      method: 'DELETE'
    }),

  deleteDocument: (id: string) =>
    request<any>(`/api/documents/${id}`, { method: 'DELETE' }),

  getDocumentStatus: (id: string) =>
    request<any>(`/api/documents/${id}/status`),

  saveDocumentMarkdown: (id: string, markdown: string) =>
    request<any>(`/api/documents/${id}/markdown`, { method: 'PUT', body: { markdown } }),

  // Shares & Direct Collaboration
  listShares: (id: string) => request<{ shares: any[] }>(`/api/documents/${id}/shares`),
  createShare: (id: string, payload: { 
    visibility: string; 
    permissions: string; 
    password?: string; 
    expires_in_days?: number;
    allow_download?: boolean;
    watermark_enabled?: boolean;
    max_access_count?: number | null;
    email?: string;
  }) => request<{ share: any }>(`/api/documents/${id}/shares`, { method: 'POST', body: payload }),
  deleteShare: (shareId: string) => request<any>(`/api/shares/${shareId}`, { method: 'DELETE' }),
  getShareLogs: (id: string) => request<{ logs: any[] }>(`/api/documents/${id}/shares/logs`),
  respondShareInvite: (shareId: string, status: 'accepted' | 'declined') => 
    request<any>(`/api/shares/${shareId}/respond`, { method: 'POST', body: { status } }),
  lockDocument: (id: string) => request<{ success?: boolean; locked?: boolean; locked_by?: string; message?: string }>(`/api/documents/${id}/lock`, { method: 'POST' }),
  unlockDocument: (id: string) => request<any>(`/api/documents/${id}/lock`, { method: 'DELETE' }),
  getPublicShareFile: (shareId: string, fileId: string, isDownload?: boolean) => 
    request<any>(`/api/shares/${shareId}/file/${fileId}${isDownload ? '?download=1' : ''}`),

  // Organization Members
  listOrgMembers: () => request<{ members: any[] }>('/api/organization/members'),
  removeOrgMember: (id: string) => request<any>(`/api/organization/members/${id}`, { method: 'DELETE' }),

  // Vault Members
  getVaultMembers: (vaultId: string) => request<{ members: any[] }>(`/api/vaults/${vaultId}/members`),
  addVaultMember: (vaultId: string, userId: string, role: string) => 
    request<any>(`/api/vaults/${vaultId}/members`, { method: 'POST', body: { user_id: userId, role } }),
  removeVaultMember: (vaultId: string, userId: string) => 
    request<any>(`/api/vaults/${vaultId}/members/${userId}`, { method: 'DELETE' }),

  // RAG Query
  query: (
    queryText: string,
    documentIds?: string[],
    topK = 5,
    documentContent?: string,
    documentFilename?: string
  ) =>
    request<{ context: string; answer?: string; raw_context?: string; sources: any[]; latency_ms: number; routed_tier?: string }>('/api/query', {
      method: 'POST',
      body: {
        query: queryText,
        document_ids: documentIds,
        top_k: topK,
        document_content: documentContent,
        document_filename: documentFilename
      },
    }),

  // API Keys (For external integrations)
  listApiKeys: () => request<any>('/api/api-keys'),
  createApiKey: (name: string) =>
    request<any>('/api/api-keys', { method: 'POST', body: { name } }),
  queryDocument(query: string, filterParentId?: string) {
    return request('/api/query', { method: 'POST', body: { query, filterParentId } });
  },

  // ── ADMIN & BILLING ────────────────────────────────────────────────────────

  getBillingPlans() {
    return request('/api/admin/plans', { method: 'GET' });
  },
  upsertBillingPlan(plan: any) {
    return request('/api/admin/plans', { method: 'POST', body: plan });
  },
  deleteBillingPlan(id: string) {
    return request(`/api/admin/plans/${id}`, { method: 'DELETE' });
  },
  getAdminUsers() {
    return request('/api/admin/users', { method: 'GET' });
  },
  createOrUpdateAdminUser(user: any) {
    return request('/api/admin/users', { method: 'POST', body: user });
  },
  deleteAdminUser(id: string) {
    return request(`/api/admin/users/${id}`, { method: 'DELETE' });
  },
  assignUserPlan(userId: string, planId: string) {
    return request(`/api/admin/users/${userId}/assign_plan`, { method: 'POST', body: { planId } });
  },
  
  createCheckoutSession(data: any) {
    return request('/api/billing/checkout', { method: 'POST', body: data });
  },
  getSharedKeyInfo: () => request<any>('/api/billing/shared-key/info'),
  getPublicBillingPlans: () => request<any>('/api/billing/plans'),
  generateSharedKey() {
    return request('/api/billing/shared-key/generate', { method: 'POST' });
  },
  linkSharedKey(sharedKey: string) {
    return request('/api/billing/shared-key/link', { method: 'POST', body: { sharedKey } });
  },
  revokeApiKey: (id: string) =>
    request<any>(`/api/api-keys/${id}`, { method: 'DELETE' }),

  // ── VAULT PROFILE & OPERATIONS ─────────────────────────────────────────────
  getVaultProfile: () => request<any>('/api/vault/profile'),
  saveVaultProfile: (data: any) => request<any>('/api/vault/profile', { method: 'PUT', body: data }),

  getOperations: () => request<any>('/api/vault/operations'),
  saveOperations: (data: any) => request<any>('/api/vault/operations', { method: 'PUT', body: data }),

  // ── VOICE CONFIG & USAGE ──────────────────────────────────────────────────
  getVoiceConfig: () => request<any>('/api/admin/voice-config'),
  saveVoiceConfig: (data: any) => request<any>('/api/admin/voice-config', { method: 'PUT', body: data }),
  testVoiceConnection: (voiceUrl: string, mcpKey: string) =>
    request<any>('/api/admin/voice-config/test', { method: 'POST', body: { voice_url: voiceUrl, mcp_key: mcpKey } }),
  getVoiceUsage: () => request<{ enabled: boolean; used: number; limit: number; plan: string; voice_base_url: string }>('/api/voice/usage'),
  queryVoice: (tool: string, args: any) => request<any>('/api/voice/query', { method: 'POST', body: { tool, arguments: args } }),

  // ── PHASE 2: INTELLIGENCE & MEMORY LAYER ─────────────────────────────────
  getGrowthLogs: () => request<{ logs: any[] }>('/api/vault/growth'),
  saveGrowthLog: (log: any) => request<any>('/api/vault/growth', { method: 'POST', body: log }),

  getFlashcards: () => request<{ cards: any[] }>('/api/vault/flashcards'),
  saveFlashcards: (cards: any[]) => request<any>('/api/vault/flashcards', { method: 'PUT', body: { cards } }),

  getVaultBots: () => request<{ bots: any[] }>('/api/vault/bots'),
  saveVaultBots: (bots: any[]) => request<any>('/api/vault/bots', { method: 'PUT', body: { bots } }),

  // ── PHASE 3: COLLECTIVE INTELLIGENCE & ECOSYSTEM ───────────────────────────
  getVaultGraph: () => request<{ vaults: any[]; documents: any[]; stats: any }>('/api/vault/graph'),

  getWorkspaceDepartments: () => request<{ departments: any[] }>('/api/workspace/departments'),
  saveWorkspaceDepartments: (departments: any[]) => request<any>('/api/workspace/departments', { method: 'POST', body: { departments } }),
  getWorkspaceAudit: () => request<{ events: any[] }>('/api/workspace/audit'),

  getDocumentVersions: (docId: string) => request<{ versions: any[] }>(`/api/documents/${docId}/versions`),
  saveDocumentVersion: (docId: string, payload: { content: string; summary?: string }) =>
    request<{ success: boolean; version: any }>(`/api/documents/${docId}/versions`, { method: 'POST', body: payload }),

  getCollectiveFeed: () => request<{ posts: any[] }>('/api/collective/feed'),
  createCollectivePost: (post: any) => request<any>('/api/collective/feed', { method: 'POST', body: { action: 'create', ...post } }),
  likeCollectivePost: (postId: string) => request<any>('/api/collective/feed', { method: 'POST', body: { action: 'like', postId } }),

  getTrustedAgents: () => request<{ agents: any[] }>('/api/agents/tokens'),
  createTrustedAgent: (payload: any) => request<any>('/api/agents/tokens', { method: 'POST', body: payload }),
  deleteTrustedAgent: (agentId: string) => request<any>(`/api/agents/tokens/${agentId}`, { method: 'DELETE' }),

  sendProactiveVoiceAlert: (payload: { message: string; target?: string; type?: string }) =>
    request<any>('/api/voice/proactive-alert', { method: 'POST', body: payload }),

  // ── PHASE 4: KNOWLEDGE ECONOMY & SUCCESSION ────────────────────────────────
  getExpertiseScore: () => request<any>('/api/vault/expertise-score'),
  recalculateExpertiseScore: () => request<any>('/api/vault/expertise-score/recalculate', { method: 'POST' }),

  getMarketplacePacks: () => request<{ packs: any[] }>('/api/marketplace/packs'),
  createMarketplacePack: (pack: any) => request<any>('/api/marketplace/packs', { method: 'POST', body: pack }),
  installMarketplacePack: (packId: string) => request<any>(`/api/marketplace/packs/${packId}/install`, { method: 'POST' }),

  getLegacyPlan: () => request<any>('/api/vault/legacy'),
  saveLegacyPlan: (data: any) => request<any>('/api/vault/legacy', { method: 'POST', body: data }),
  exportLegacyVault: () => request<any>('/api/vault/legacy/export', { method: 'POST' }),

  // ── ERIX AI PROXY GATEWAY (ADMIN) ──────────────────────────────────────────
  getAiProxyConfig: () => request<any>('/api/admin/ai-proxy'),
  saveAiProxyConfig: (data: any) => request<any>('/api/admin/ai-proxy', { method: 'PUT', body: data }),
  testAiProxyConnection: (payload?: any) => request<any>('/api/admin/ai-proxy/test', { method: 'POST', body: payload || {} }),

  // ── ERIX PAAM & MCP PLATFORM (COMMERCIAL SPEC V1.0) ────────────────────────
  getModels: () => request<{ object: string; data: any[] }>('/v1/models'),
  testChatCompletion: (prompt: string, model = 'erix-one', token?: string) =>
    request<any>('/v1/chat/completions', {
      method: 'POST',
      body: {
        model,
        messages: [{ role: 'user', content: prompt }],
        stream: false
      },
      headers: token ? { 'Authorization': `Bearer ${token}` } : undefined
    }),
  testMcpHandshake: async () => {
    const res = await fetch('/mcp/sse', { headers: { 'Accept': 'text/event-stream' } });
    return { ok: res.ok, status: res.status, headers: Object.fromEntries(res.headers.entries()) };
  },

  // Stats
  getStats: () => request<any>('/api/stats'),

  // ── DIGITAL NOTARY & BLOCKCHAIN PROOF-OF-EXISTENCE ────────────────────────
  certifyDocument: (payload: {
    document_id: string;
    document_name?: string;
    signer_name: string;
    signer_email?: string;
    signer_role?: string;
    signing_reason: string;
    signature_data: string;
    signature_type?: string;
    notes?: string;
  }) => request<{ success: boolean; certificate: any }>('/api/notary/certify', { method: 'POST', body: payload }),

  listAllCertificates: () => request<{ certificates: any[] }>('/api/notary/certificates'),

  getDocumentCertificates: (docId: string) => request<{ certificates: any[] }>(`/api/documents/${docId}/notary`),

  verifyCertificate: (certId: string) => request<{ valid: boolean; certificate?: any; error?: string }>(`/api/notary/verify/${certId}`),

  revokeCertificate: (certId: string, reason?: string) =>
    request<{ success: boolean; certificate: any }>(`/api/notary/revoke/${certId}`, { method: 'POST', body: { reason } }),

  // ── ROLE-BASED MATRIX OWNERSHIP & DUAL PHYSICAL ARCHIVE ────────────────────
  getPositions: () => request<{ positions: any[] }>('/api/v1/positions'),

  createPosition: (pos: any) =>
    request<{ success: boolean; position: any }>('/api/v1/positions', { method: 'POST', body: pos }),

  handoverPosition: (positionId: string, payload: {
    new_occupant_user_id: string;
    new_occupant_name?: string;
    effective_date?: number;
    handover_note?: string;
    retain_read_access_for_predecessor?: boolean;
  }) => request<{
    success: boolean;
    message: string;
    position: any;
    handover_log: any;
  }>(`/api/v1/positions/${positionId}/handover`, { method: 'POST', body: payload }),

  getPositionHandovers: (positionId: string) =>
    request<{ handovers: any[] }>(`/api/v1/positions/${positionId}/handovers`),

  evaluateDocumentPermissions: (docId: string) =>
    request<any>(`/api/v1/documents/${docId}/evaluate-permissions`),

  getDocumentGovernance: (docId: string) =>
    request<any>(`/api/v1/documents/${docId}/governance`),

  updateDocumentMatrixAcl: (docId: string, payload: {
    owner_position_id: string;
    sign_authority_position_id?: string;
    review_matrix?: any[];
    inherit_on_occupant_change?: boolean;
  }) => request<{ success: boolean; matrix_acl: any }>(`/api/v1/documents/${docId}/matrix-acl`, { method: 'POST', body: payload }),

  updatePhysicalLocation: (docId: string, payload: {
    warehouse_name?: string;
    aisle?: string;
    shelf?: string;
    box_number?: string;
    folder_label?: string;
    archive_code?: string;
    has_physical_copy?: boolean;
    note?: string;
  }) => request<{ success: boolean; physical_archive: any }>(`/api/v1/documents/${docId}/physical-location`, { method: 'PATCH', body: payload }),

  borrowPhysicalDocument: (docId: string, payload: {
    borrower_position_id?: string;
    borrower_user_id?: string;
    borrower_name: string;
    due_date?: number;
    purpose: string;
  }) => request<{ success: boolean; message: string; physical_archive: any }>(`/api/v1/documents/${docId}/physical-borrow`, { method: 'POST', body: payload }),

  returnPhysicalDocument: (docId: string, payload?: { note?: string }) =>
    request<{ success: boolean; message: string; physical_archive: any }>(`/api/v1/documents/${docId}/physical-return`, { method: 'POST', body: payload || {} }),

  getPhysicalArchive: (docId: string) =>
    request<{ physical_archive: any }>(`/api/v1/documents/${docId}/physical-archive`),
};

export { request as apiFetch };

